import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Menu, 
  X, 
  User, 
  TrendingUp, 
  DollarSign, 
  Settings, 
  LogOut, 
  ChevronDown,
  Share2,
  Shield,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { clearUser } from '../store/slices/authSlice';
import Images from '../utils/Images.jsx';

const AuthenticatedNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [, setShowNotifications] = useState(false);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(() => {
    return !localStorage.getItem('welcomeMessageDismissed');
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  
  const { user } = useSelector((state) => state.auth);
  
  // Extract publisher level from channel_ratings array
  const getPublisherLevel = () => {
    // Always default to Level 1 for approved users
    if (!user) {
      return { level: 1, level_name: "Basic Publisher" };
    }
    
    // Try to get level from channel_ratings if available
    if (user?.channel_ratings && Array.isArray(user.channel_ratings)) {
      const levelMetadata = user.channel_ratings.find(
        (rating) => rating?.platform === "metadata" && 
                    rating?.channel_data?.type === "publisher_level"
      );
      
      if (levelMetadata?.channel_data) {
        return {
          level: levelMetadata.channel_data.level || 1,
          level_name: levelMetadata.channel_data.level_name || "Basic Publisher",
        };
      }
    }
    
    // Default to Level 1 for all approved users
    return { level: 1, level_name: "Basic Publisher" };
  };
  
  const publisherLevel = getPublisherLevel();
  const isProfilePage = location.pathname === "/profile";
  
  // Check if user is approved/active
  // Show badge for users who are approved or active, or if they have any affiliate status
  const isApprovedUser = user && (
    user?.affiliate_status === "full_approval" || 
    user?.affiliate_status === "limited_approval" || 
    user?.is_active === 1 || 
    user?.is_active === true ||
    user?.affiliate_status // Show for any affiliate status (pending, approved, etc.)
  );

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-menu') && !event.target.closest('.notification-menu')) {
        setShowUserMenu(false);
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      // Store current scroll position
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    return () => {
      // Cleanup on unmount
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleLogout = () => {
    dispatch(clearUser());
    navigate('/');
    setShowUserMenu(false);
  };

  const navLinks = [
    { name: "Home", href: "/", icon: null },
    { name: "Trending", href: "/trending-authenticated", icon: TrendingUp },
    { name: "Earnings", href: "/earnings", icon: DollarSign },
    { name: "Applied Deals", href: "/applied-deals", icon: Share2 },
  ];

  const userMenuItems = [
    { name: "Dashboard", href: "/dashboard", icon: TrendingUp },
    { name: "Profile", href: "/profile", icon: User },
    { name: "Settings", href: "/profile", icon: Settings },
  ];



  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || location.pathname === '/dashboard'
          ? 'border-b border-gray-200 shadow-lg backdrop-blur-md bg-white/95' 
          : 'bg-transparent'
      }`}>
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center"
          >
            <Link to="/" className="flex items-center">
              <img 
                src={Images.logo} 
                alt="DealBoxx Logo" 
                className="w-auto h-10"
              />
            </Link>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden items-center space-x-8 md:flex">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={link.href}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors duration-300 hover:text-[#FF7F7F] ${
                    isScrolled ? 'text-gray-700' : 'text-gray-700'
                  }`}
                >
                  {link.icon && <link.icon className="w-4 h-4" />}
                  {link.name}
                </Link>
              </motion.div>
            ))}
            
            {/* Publisher Level Badge - Hidden on profile page, show for approved users */}
            {!isProfilePage && user && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-default"
                title={`Publisher Level ${publisherLevel.level} - ${publisherLevel.level_name}`}
              >
                <Shield className="w-4 h-4 text-white flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-bold text-white leading-tight whitespace-nowrap">Level {publisherLevel.level}</span>
                  <span className="text-[10px] text-white/90 leading-tight whitespace-nowrap truncate max-w-[100px]">{publisherLevel.level_name}</span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Right Side Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="flex gap-4 items-center"
          >
           

            {/* User Menu */}
            <div className="relative user-menu">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex gap-2 items-center p-2 rounded-lg transition-colors duration-300 hover:bg-gray-100"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-[#FF7F7F] to-[#ff6b6b] rounded-full flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">
                    {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </button>

              {/* User Dropdown */}
              <AnimatePresence>
                {showUserMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden absolute right-0 mt-3 w-72 bg-white rounded-2xl border border-gray-100 shadow-2xl backdrop-blur-sm"
                  >
                    {/* Header with better styling */}
                    <div className="p-6 bg-gradient-to-r from-[#FF7F7F] to-pink-500">
                      <div className="flex gap-4 items-center">
                        <div className="flex justify-center items-center w-14 h-14 rounded-full border-2 backdrop-blur-sm bg-white/20 border-white/30">
                          <span className="text-xl font-bold text-white">
                            {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-white">
                            {user?.account_display_name}
                          </h3>
                          <p className="text-sm text-white/90">{user?.email}</p>
                          <div className="inline-block px-2 py-1 mt-1 rounded-full bg-white/20">
                            <span className="text-xs font-medium text-white">Active Affiliate</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Menu Items */}
                    <div className="py-3">
                      {userMenuItems.map((item, index) => (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setShowUserMenu(false)}
                          className="flex gap-4 items-center px-6 py-4 text-gray-700 transition-all duration-200 hover:bg-gray-50 hover:text-[#FF7F7F] group"
                        >
                          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 group-hover:bg-[#FF7F7F]/10 transition-colors duration-200">
                            <item.icon className="w-4 h-4 text-gray-600 group-hover:text-[#FF7F7F]" />
                          </div>
                          <span className="font-medium">{item.name}</span>
                        </Link>
                      ))}
                      
                      <div className="mx-6 my-3 border-t border-gray-100"></div>
                      
                      <button
                        onClick={handleLogout}
                        className="flex gap-4 items-center px-6 py-4 w-full text-red-600 transition-all duration-200 hover:bg-red-50 group"
                      >
                        <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-lg transition-colors duration-200 group-hover:bg-red-200">
                          <LogOut className="w-4 h-4 text-red-600" />
                        </div>
                        <span className="font-medium">Sign Out</span>
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              onClick={() => setIsOpen(!isOpen)}
              className={`md:hidden p-3 rounded-xl transition-all duration-300 ${
                isOpen 
                  ? 'bg-[#FF7F7F] text-white shadow-lg' 
                  : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF7F7F]'
              }`}
              aria-label="Toggle menu"
            >
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </motion.div>
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/50 md:hidden"
              style={{ 
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                height: '100vh',
                width: '100vw'
              }}
            />
            
            {/* Mobile Menu Panel - Slide from Right */}
            <motion.div
              initial={{ x: "100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "100%", opacity: 0 }}
              transition={{ 
                type: "spring", 
                damping: 30, 
                stiffness: 300,
                duration: 0.3 
              }}
              className="overflow-y-auto fixed top-0 right-0 z-[70] w-80 h-screen bg-white shadow-2xl md:hidden"
              style={{ 
                position: 'fixed',
                top: 0,
                right: 0,
                height: '100vh',
                width: '320px'
              }}
            >
              {/* Header */}
              <div className="sticky top-0 z-[80] px-6 py-4 bg-white border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 text-gray-400 rounded-lg transition-colors hover:text-gray-600 hover:bg-gray-100"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* User Profile Section */}
              <div className="px-6 py-6 bg-gradient-to-r from-[#FF7F7F] to-pink-500">
                <div className="flex gap-4 items-center">
                  <div className="flex justify-center items-center w-16 h-16 rounded-full border-2 backdrop-blur-sm bg-white/20 border-white/30">
                    <span className="text-2xl font-bold text-white">
                      {user?.first_name?.charAt(0)?.toUpperCase() || 'U'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white">
                      {user?.account_display_name || `${user?.first_name} ${user?.last_name}`}
                    </h3>
                    <p className="text-sm text-white/90">{user?.email}</p>
                    <div className="flex gap-2 items-center mt-2">
                      <div className="inline-block px-3 py-1 rounded-full bg-white/20">
                        <span className="text-xs font-medium text-white">Active Affiliate</span>
                      </div>
                      {/* Publisher Level Badge - Hidden on profile page */}
                      {!isProfilePage && user && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/20 border border-white/30">
                          <Shield className="w-3 h-3 text-white" />
                          <span className="text-xs font-bold text-white">Level {publisherLevel.level}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Navigation Content */}
              <div className="px-6 py-6 space-y-6">
                {/* Main Navigation */}
                <div>
                  <h4 className="px-3 py-2 mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                    Navigation
                  </h4>
                  <div className="space-y-2">
                    {navLinks.map((link, index) => (
                      <motion.div
                        key={link.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                      >
                        <Link
                          to={link.href}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-4 px-4 py-3 text-gray-700 rounded-xl transition-all duration-200 hover:bg-gray-50 hover:text-[#FF7F7F] group"
                        >
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 group-hover:bg-[#FF7F7F]/10 transition-colors duration-200">
                            {link.icon && <link.icon className="w-5 h-5 text-gray-600 group-hover:text-[#FF7F7F]" />}
                          </div>
                          <span className="text-lg font-medium">{link.name}</span>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Account Section */}
                <div>
                  <h4 className="px-3 py-2 mb-3 text-xs font-semibold tracking-wider text-gray-500 uppercase">
                    Account
                  </h4>
                  <div className="space-y-2">
                    {userMenuItems.map((item, index) => (
                      <motion.div
                        key={item.name}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: (navLinks.length + index) * 0.1 }}
                      >
                        <Link
                          to={item.href}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center gap-4 px-4 py-3 text-gray-700 rounded-xl transition-all duration-200 hover:bg-gray-50 hover:text-[#FF7F7F] group"
                        >
                          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 group-hover:bg-[#FF7F7F]/10 transition-colors duration-200">
                            <item.icon className="w-5 h-5 text-gray-600 group-hover:text-[#FF7F7F]" />
                          </div>
                          <span className="text-lg font-medium">{item.name}</span>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Sign Out Button */}
                <div className="pt-6 border-t border-gray-100">
                  <motion.button
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: (navLinks.length + userMenuItems.length) * 0.1 }}
                    onClick={handleLogout}
                    className="flex gap-4 items-center px-4 py-3 w-full text-red-600 rounded-xl transition-all duration-200 hover:bg-red-50 group"
                  >
                    <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg transition-colors duration-200 group-hover:bg-red-200">
                      <LogOut className="w-5 h-5 text-red-600" />
                    </div>
                    <span className="text-lg font-medium">Sign Out</span>
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>

    {/* Welcome Banner */}
    <AnimatePresence>
      {showWelcomeMessage && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.6 }}
          className="fixed top-16 left-0 right-0 z-40 bg-gradient-to-r from-[#FF7F7F] to-pink-500 shadow-lg"
        >
          <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-8 h-8 rounded-full bg-white/20">
                  <span className="text-sm font-bold text-white">
                    {user?.first_name?.charAt(0)?.toUpperCase() || "U"}
                  </span>
                </div>
                <div className="text-white">
                  <p className="text-sm font-medium">
                    Welcome back, {user?.first_name}!
                  </p>
                  <p className="text-xs opacity-90">
                    Ready to promote amazing deals?
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowWelcomeMessage(false);
                  localStorage.setItem('welcomeMessageDismissed', 'true');
                }}
                className="p-1 rounded-full transition-colors duration-200 hover:bg-white/20"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default AuthenticatedNavbar;
